// src/features/streams/useStreams.ts

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { MEDIA_QUERY_KEYS } from '@/lib/constants/media';
import { StreamApi } from './StreamApi';
import type { StreamViewData } from '@/lib/types/Stream';

/** Fetches all streams for current customer. */
export function useStreams(options?: { enabled?: boolean; refetchOnMount?: boolean | 'always' }) {
  const { selectedCId } = useAuth();

  const query = useQuery({
    queryKey: MEDIA_QUERY_KEYS.streams(selectedCId ?? 0),
    queryFn: async () => {
      const result = await StreamApi.getStreams(selectedCId!);
      return result.data;
    },
    enabled: (options?.enabled ?? true) && selectedCId != null,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    // Default (stale-based) for existing callers; cache-tolerant consumers
    // (playlist views) pass false to skip mount refetches entirely.
    refetchOnMount: options?.refetchOnMount ?? true,
  });

  const streams: StreamViewData[] = useMemo(
    () => query.data ?? [],
    [query.data],
  );

  return {
    streams,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}
